import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { PageHeader } from "../components/PageHeader";
import { ExtractButton } from "../components/ExtractButton";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Buildings, WarningCircle, SquaresFour, List as ListIcon, MapPin, Phone, User, Briefcase } from "@phosphor-icons/react";

const emptyForm = {
  ragione_sociale: "", partita_iva: "", codice_fiscale: "", forma_giuridica: "",
  referente: "", email: "", pec: "", telefono: "",
  indirizzo: "", citta: "", cap: "",
  iscrizione_cciaa: "", categoria_soa: "", durc_scadenza: "", note: "",
};

// Verifica idoneità tecnico-professionale (art. 97 e all. XVII D.Lgs 81/2008)
const TIPI_DOCUMENTO = [
  "POS", "Assicurazione RCT/RCO", "Visura CCIAA", "DVR",
  "Iscrizione Cassa Edile", "Idoneità sanitaria", "Formazione lavoratori", "Altro",
];

const giorniA = (d) => {
  if (!d) return null;
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${String(d).slice(0, 10)}T00:00:00`) - oggi) / 86400000);
};

const scadenzaCls = (gg) => (gg < 0
  ? "text-red-700 bg-red-50 border-red-200"
  : gg <= 30 ? "text-amber-700 bg-amber-50 border-amber-200"
  : "text-emerald-700 bg-emerald-50 border-emerald-200");

/** Documento (DURC o idoneità) più critico dell'impresa, per il badge in elenco. */
const docPiuCritico = (c) => {
  const tutti = [
    ...(c.durc_scadenza ? [{ tipo: "DURC", scadenza: c.durc_scadenza }] : []),
    ...((c.documenti_idoneita || []).filter((d) => d.scadenza)),
  ].map((d) => ({ ...d, gg: giorniA(d.scadenza) })).filter((d) => d.gg !== null && !Number.isNaN(d.gg));
  if (!tutti.length) return null;
  tutti.sort((a, b) => a.gg - b.gg);
  const peggiore = tutti[0];
  const label = peggiore.gg < 0
    ? `${peggiore.tipo} scaduto da ${Math.abs(peggiore.gg)}gg`
    : peggiore.gg <= 30 ? `${peggiore.tipo} in scadenza (${peggiore.gg}gg)`
    : `Documenti ok (${peggiore.tipo} ${peggiore.gg}gg)`;
  return { label, cls: scadenzaCls(peggiore.gg), n: tutti.length };
};

export default function Imprese() {
  const [items, setItems] = useState([]);
  const [pratiche, setPratiche] = useState([]);
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("imprese.view") || "cards");
  const changeViewMode = (m) => { setViewMode(m); try { localStorage.setItem("imprese.view", m); } catch { /* ignore */ } };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [docs, setDocs] = useState([]);

  const load = async () => {
    const { data } = await api.get("/imprese");
    setItems(data);
    try { const r = await api.get("/pratiche"); setPratiche(r.data || []); } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDocs([]); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...Object.fromEntries(Object.keys(emptyForm).map((k) => [k, c[k] || ""])) });
    setDocs((c.documenti_idoneita || []).map((d) => ({ tipo: "", numero: "", rilascio: "", scadenza: "", note: "", ...d })));
    setOpen(true);
  };

  const aggiungiDoc = () => setDocs([...docs, { tipo: "POS", numero: "", rilascio: "", scadenza: "", note: "" }]);
  const cambiaDoc = (i, campo, valore) => setDocs(docs.map((d, idx) => (idx === i ? { ...d, [campo]: valore } : d)));
  const rimuoviDoc = (i) => setDocs(docs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ragione_sociale.trim()) { toast.error("Ragione sociale richiesta"); return; }
    const payload = { ...form, documenti_idoneita: docs.filter((d) => d.tipo) };
    try {
      if (editing) {
        await api.put(`/imprese/${editing.id}`, payload);
        toast.success("Impresa aggiornata");
      } else {
        await api.post("/imprese", payload);
        toast.success("Impresa creata");
      }
      setOpen(false);
      load();
    } catch { toast.error("Errore salvataggio"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Eliminare l'impresa "${c.ragione_sociale}"?`)) return;
    try {
      await api.delete(`/imprese/${c.id}`);
      toast.success("Impresa eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  const filtered = items.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return `${c.ragione_sociale} ${c.partita_iva} ${c.referente} ${c.citta} ${c.email} ${c.pec}`.toLowerCase().includes(q);
  });

  return (
    <div className="p-4 md:p-8 w-full max-w-[1400px] mx-auto" data-testid="imprese-page">
      <PageHeader
        eyebrow="Anagrafica"
        title="Imprese"
        description="Rubrica delle imprese esecutrici. Con estrazione automatica da visura camerale e DURC."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-impresa-button"><Plus size={16} weight="bold" /> Nuova impresa</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Modifica impresa" : "Nuova impresa"}</DialogTitle></DialogHeader>
              {!editing && (
                <div className="mb-3 p-3 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-600">
                    Carica visura camerale, DURC o certificato SOA: l&apos;AI compila i campi per te.
                  </div>
                  <ExtractButton
                    endpoint="impresa"
                    testid="extract-impresa-button"
                    onExtracted={(d) => {
                      setForm((prev) => ({
                        ...prev,
                        ...Object.fromEntries(Object.keys(emptyForm).map((k) => [k, d[k] || prev[k]])),
                      }));
                    }}
                  />
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-2"><Label>Ragione sociale *</Label><Input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} required data-testid="impresa-ragione-sociale-input" /></div>
                  <div className="space-y-2"><Label>Forma giuridica</Label><Input value={form.forma_giuridica} onChange={(e) => setForm({ ...form, forma_giuridica: e.target.value })} placeholder="SRL, SNC..." /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} data-testid="impresa-piva-input" /></div>
                  <div className="space-y-2"><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Referente / Legale rappresentante</Label><Input value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="space-y-2"><Label>PEC</Label><Input type="email" value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Indirizzo sede</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-2"><Label>Città</Label><Input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} /></div>
                  <div className="space-y-2"><Label>CAP</Label><Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Iscrizione CCIAA / REA</Label><Input value={form.iscrizione_cciaa} onChange={(e) => setForm({ ...form, iscrizione_cciaa: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Categoria SOA</Label><Input value={form.categoria_soa} onChange={(e) => setForm({ ...form, categoria_soa: e.target.value })} placeholder="OG1, OS30..." /></div>
                </div>
                <div className="space-y-2"><Label>Scadenza DURC</Label><Input type="date" value={form.durc_scadenza} onChange={(e) => setForm({ ...form, durc_scadenza: e.target.value })} data-testid="impresa-durc-input" /></div>

                <div className="pt-3 border-t border-slate-200 space-y-3" data-testid="documenti-idoneita-section">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Label>Verifica idoneità tecnico-professionale</Label>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        POS, assicurazione, visura, DVR… Ti avviso in dashboard 30 giorni prima della scadenza.
                      </p>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={aggiungiDoc} data-testid="aggiungi-documento-btn">
                      <Plus size={14} /> Documento
                    </Button>
                  </div>
                  {docs.length === 0 && (
                    <p className="text-xs text-slate-500">Nessun documento registrato oltre al DURC.</p>
                  )}
                  {docs.map((d, i) => {
                    const gg = giorniA(d.scadenza);
                    return (
                      <div key={i} className="p-3 rounded-md border border-slate-200 space-y-2" data-testid={`documento-row-${i}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[11px]">Tipo</Label>
                            <select
                              className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                              value={d.tipo}
                              onChange={(e) => cambiaDoc(i, "tipo", e.target.value)}
                              data-testid={`documento-tipo-${i}`}
                            >
                              {TIPI_DOCUMENTO.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px]">Numero / riferimento</Label>
                            <Input value={d.numero} onChange={(e) => cambiaDoc(i, "numero", e.target.value)}
                              data-testid={`documento-numero-${i}`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px]">Rilascio</Label>
                            <Input type="date" value={(d.rilascio || "").slice(0, 10)}
                              onChange={(e) => cambiaDoc(i, "rilascio", e.target.value)} data-testid={`documento-rilascio-${i}`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px]">Scadenza</Label>
                            <Input type="date" value={(d.scadenza || "").slice(0, 10)}
                              onChange={(e) => cambiaDoc(i, "scadenza", e.target.value)} data-testid={`documento-scadenza-${i}`} />
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          {gg !== null && !Number.isNaN(gg) ? (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${scadenzaCls(gg)}`}>
                              {gg < 0 ? `scaduto da ${Math.abs(gg)}gg` : gg === 0 ? "scade oggi" : `valido ${gg}gg`}
                            </span>
                          ) : <span className="text-[10px] text-slate-400">senza scadenza</span>}
                          <Button type="button" size="sm" variant="ghost" onClick={() => rimuoviDoc(i)} data-testid={`documento-rimuovi-${i}`}>
                            <Trash size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="space-y-2"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="impresa-submit-button">Salva</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca per ragione sociale, P.IVA, città..." className="pl-9" data-testid="imprese-search-input" />
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1" data-testid="view-toggle">
          <button onClick={() => changeViewMode("cards")} data-testid="view-cards" className={`px-2.5 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${viewMode === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}><SquaresFour size={14} /> Schede</button>
          <button onClick={() => changeViewMode("list")} data-testid="view-list" className={`px-2.5 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}><ListIcon size={14} /> Lista</button>
        </div>
        <div className="text-xs font-mono text-slate-500 ml-auto">{filtered.length} imprese</div>
      </div>

      {viewMode === "cards" ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((c) => {
          const durc = docPiuCritico(c);
          const imprPratiche = pratiche.filter((p) => (p.imprese_ids || []).includes(c.id));
          const attive = imprPratiche.filter((p) => p.stato !== "completata" && p.stato !== "annullata").length;
          const completate = imprPratiche.filter((p) => p.stato === "completata").length;
          return (
            <Card key={c.id} className="p-5 border-slate-200 shadow-none hover:border-slate-300 hover:shadow-md transition-all" data-testid={`impresa-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center"><Buildings size={18} className="text-slate-700" /></div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-impresa-${c.id}`}><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(c)}><Trash size={14} className="text-red-600" /></Button>
                </div>
              </div>
              <div className="font-heading font-semibold text-slate-900 truncate">{c.ragione_sociale}</div>
              <div className="text-[10px] text-slate-500 mb-3 font-mono uppercase tracking-widest">{c.forma_giuridica || "impresa"}</div>
              <div className="space-y-1 text-xs text-slate-600">
                {c.referente && <div className="flex items-center gap-1.5 truncate"><User size={12} className="text-slate-400 flex-shrink-0" /><span className="truncate">{c.referente}</span></div>}
                {c.partita_iva && <div className="flex items-center gap-1.5 truncate font-mono"><span className="text-[10px] text-slate-400 uppercase">P.IVA</span> {c.partita_iva}</div>}
                {c.telefono && <a href={`tel:${c.telefono}`} className="flex items-center gap-1.5 truncate font-mono hover:text-blue-600" onClick={(e) => e.stopPropagation()}><Phone size={12} className="text-slate-400 flex-shrink-0" /><span className="truncate">{c.telefono}</span></a>}
                {c.citta && <div className="flex items-center gap-1.5 truncate"><MapPin size={12} className="text-slate-400 flex-shrink-0" /><span className="truncate">{c.citta}{c.cap && ` (${c.cap})`}</span></div>}
                {c.categoria_soa && <div className="truncate text-[11px]">SOA: {c.categoria_soa}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                <Briefcase size={12} className="text-slate-400" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Attive: {attive}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Completate: {completate}</span>
                {durc && (
                  <span className={`ml-auto inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${durc.cls}`} title="Documenti di idoneità"><WarningCircle size={10} /> {durc.label}</span>
                )}
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full p-12 text-center text-sm text-slate-500">
            Nessuna impresa trovata. <Button variant="link" onClick={openNew}>Aggiungi la prima</Button>
          </div>
        )}
      </div>
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed" style={{ minWidth: 1050 }}>
              <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left">
                
<th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">
Impresa</th>
                
<th className="w-[130px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">
P.IVA</th>
                
<th className="w-[140px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">
Telefono</th>
                
<th className="w-[160px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">
Città</th>
                
<th className="w-[90px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600 text-center">
Pratiche</th>
                
<th className="w-[120px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">
Documenti</th>
                
<th className="w-[80px] p-3" />

              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => {
                  const durc = docPiuCritico(c);
                  const imprPratiche = pratiche.filter((p) => (p.imprese_ids || []).includes(c.id));
                  const attive = imprPratiche.filter((p) => p.stato !== "completata" && p.stato !== "annullata").length;
                  const completate = imprPratiche.filter((p) => p.stato === "completata").length;
                  return (
                    <tr key={c.id} className="hover:bg-slate-50" data-testid={`impresa-row-${c.id}`}>
                      <td className="p-3"><div className="font-semibold text-slate-900">{c.ragione_sociale}</div><div className="text-[10px] font-mono uppercase text-slate-500">{c.forma_giuridica || "impresa"}</div></td>
                      <td className="p-3 text-xs font-mono text-slate-600">{c.partita_iva || "—"}</td>
                      <td className="p-3 text-xs font-mono text-slate-600">{c.telefono || "—"}</td>
                      <td className="p-3 text-xs text-slate-600">{c.citta || "—"}</td>
                      <td className="p-3 text-center"><div className="inline-flex gap-1"><span className="text-[10px] font-mono uppercase text-amber-800 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">A: {attive}</span><span className="text-[10px] font-mono uppercase text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">C: {completate}</span></div></td>
                      <td className="p-3">{durc ? <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${durc.cls}`}><WarningCircle size={10} /> {durc.label}</span> : <span className="text-[10px] text-slate-400">—</span>}</td>
                      <td className="p-3 text-right whitespace-nowrap"><Button variant="ghost" size="sm" onClick={() => openEdit(c)}><PencilSimple size={14} /></Button><Button variant="ghost" size="sm" onClick={() => del(c)}><Trash size={14} className="text-red-600" /></Button></td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-sm text-slate-500">Nessuna impresa</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
